// src/screens/LiveStats.tsx
import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient";
import {
  PlayCircle,
  BarChart2,
  Activity,
  Trash2,
  Pencil,
  Plus,
  X,
  Loader2,
  ArrowLeft,
} from "lucide-react";
import { useTranslation as useTranslationOrig } from "react-i18next";

// --- i18n safe hook (évite TS2589) + helper string + namespace
type RawT = (key: string, options?: Record<string, unknown>) => any;
const useTranslationSafe = (ns?: string) =>
  (useTranslationOrig as unknown as (ns?: any) => { t: RawT })(ns);

// --- Types ---
type Side = "team" | "opponent";

type UndoAction =
  | {
      side: Side;
      type: "stat";
      player_number: number;
      stat: keyof PlayerStatTotal;
    }
  | {
      side: Side;
      type: "shot";
      player_number: number;
      made: boolean;
      value: 1 | 2 | 3;
      shot_id?: string;
    };

type Player = {
  firstName: string;
  lastName: string;
  number: number;
  position: string;
  photoUrl?: string;
};

type StatType =
  | "points"
  | "rebounds"
  | "assists"
  | "steals"
  | "blocks"
  | "turnovers"
  | "personal_fouls"
  | "shots_attempted"
  | "shots_made";

type PlayerStatTotal = {
  player_number: number;
  points: number;
  rebounds: number;
  assists: number;
  steals: number;
  blocks: number;
  turnovers: number;
  shots_attempted: number;
  shots_made: number;
  ft_made: number;
  ft_attempted: number;
  three_made: number;
  three_attempted: number;
  personal_fouls: number;
};

// Ligne spéciale pour les stats "équipe adverse" sans joueurs
const OPP_TEAM_PLAYER_NUMBER = 0; // player_number = 0 => Équipe adverse

export const LiveStats = () => {
  const { match_id } = useParams();
  const navigate = useNavigate();

  // i18n
  const { t: tRaw } = useTranslationSafe("livestats");
  const tr = useCallback(
    (key: string, options?: Record<string, unknown>): string => {
      const out = tRaw(key, options);
      if (typeof out === "string") return out;
      if (Array.isArray(out)) return out.join("");
      return String(out ?? "");
    },
    [tRaw]
  );

  // --- helper: lock scroll behind modal (capacitor-friendly)
  const lockBodyScroll = useCallback((lock: boolean) => {
    if (typeof document === "undefined") return;
    const body = document.body;
    if (lock) {
      body.style.overflow = "hidden";
      body.style.touchAction = "none";
    } else {
      body.style.overflow = "";
      body.style.touchAction = "";
    }
  }, []);

  // ----- HOME / OPPONENT TOGGLE -----
  const [currentSide, setCurrentSide] = useState<Side>("team");

  // ----- EFFECTIFS -----
  const [players, setPlayers] = useState<Player[]>([]);
  const [opponentPlayers, setOpponentPlayers] = useState<Player[]>([]);

  // ----- SCORE -----
  const [score, setScore] = useState({ team: 0, opponent: 0 });

  // ----- SÉLECTION DE JOUEUR -----
  const [selectedPlayer, setSelectedPlayer] = useState<Player | null>(null); // mon équipe
  const [selectedOpponentPlayer, setSelectedOpponentPlayer] =
    useState<Player | null>(null); // adversaire

  // ----- STATS -----
  const [stats, setStats] = useState<Record<number, PlayerStatTotal>>({}); // mon équipe
  const [opponentStats, setOpponentStats] = useState<Record<number, PlayerStatTotal>>(
    {}
  ); // adversaire

  // ----- TERRAIN & TIRS (réservé à mon équipe) -----
  const [showCourt, setShowCourt] = useState(false);
  const [pendingShot, setPendingShot] = useState<{
    made: boolean;
    value: 1 | 2 | 3;
  } | null>(null);
  const [feedbackShot, setFeedbackShot] = useState<{
    x: number;
    y: number;
    made: boolean;
  } | null>(null);

  // ----- HISTORIQUE -----
  const [actionHistory, setActionHistory] = useState<UndoAction[]>([]);
  const [activeTab, setActiveTab] = useState<"live" | "stats" | "history">("live");

  // ----- SAISIE MOBILE "STAT D'ABORD" (pas de scroll) -----
  // On choisit d'abord le type de stat (bouton), puis le joueur dans une modale,
  // puis (si tir 2/3 pts) le terrain s'ouvre pour positionner le tir.
  type PendingAction =
    | { kind: "shot"; made: boolean; value: 1 | 2 | 3 }
    | { kind: "stat"; type: StatType };
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);
  const [showPlayerPicker, setShowPlayerPicker] = useState(false);
  const [dispatchTick, setDispatchTick] = useState(0);

  const openPlayerPicker = (action: PendingAction) => {
    setPendingAction(action);
    setShowPlayerPicker(true);
  };

  const [rosterLoading, setRosterLoading] = useState(false);
  const [teamId, setTeamId] = useState<string | null>(null);

  const sum = (key: keyof PlayerStatTotal, side: Side) =>
    Object.values(side === "team" ? stats : opponentStats).reduce(
      (t, s) => t + (s?.[key] || 0),
      0
    );

  type EditablePlayer = Player & { active: boolean };

  const [showRosterEditor, setShowRosterEditor] = useState(false);
  const [tempPlayers, setTempPlayers] = useState<EditablePlayer[]>([]);

  // Helpers côté UI
  const activePlayers = currentSide === "team" ? players : opponentPlayers;
  const activeStats = currentSide === "team" ? stats : opponentStats;

  const setActiveSelectedPlayer = (p: Player | null) => {
    if (currentSide === "team") setSelectedPlayer(p);
    else setSelectedOpponentPlayer(p);
  };

  // Ouvre l’éditeur avec l’état courant
  const openRosterEditor = async () => {
    setShowRosterEditor(true);
    setRosterLoading(true);

    type TeamRow = { id: string; players: Player[] | null };

    // fusion par numéro (le dernier écrase le précédent)
    const mergeByNumber = (...lists: (Player[] | undefined)[]) => {
      const map = new Map<number, Player>();
      for (const list of lists) {
        if (!Array.isArray(list)) continue;
        for (const p of list) {
          if (!p || typeof p.number !== "number") continue;
          map.set(p.number, p);
        }
      }
      return Array.from(map.values());
    };

    try {
      // Pour l’adversaire : pas de fetch Supabase, on reste 100% manuel
      if (currentSide === "opponent") {
        const fullRoster = mergeByNumber(opponentPlayers);
        const activeNums = new Set(opponentPlayers.map((p) => p.number));
        const editable = fullRoster.map((p) => ({
          ...p,
          active: activeNums.has(p.number),
        })) as EditablePlayer[];
        setTempPlayers(editable);
        return;
      }

      // Pour mon équipe : on va chercher la team dans Supabase
      const { data: userData } = await supabase.auth.getUser();
      const user = userData?.user;

      let teamRow: TeamRow | null = null;

      // 1) Priorité au team_id du match
      if (teamId) {
        const { data, error } = await supabase
          .from("teams")
          .select("id, players")
          .eq("id", teamId)
          .single<TeamRow>();
        if (!error && data) teamRow = data;
        else if (error) console.error("teams by id error:", error);
      }

      // 2) Fallback: 1ère équipe de l'utilisateur
      if (!teamRow && user) {
        const { data, error } = await supabase
          .from("teams")
          .select("id, players")
          .eq("user_id", user.id)
          .limit(1)
          .maybeSingle<TeamRow>();
        if (!error) teamRow = data ?? null;
        else console.error("teams by user_id error:", error);
      }

      // 3) Roster depuis teams.players (JSONB)
      const teamPlayers = Array.isArray(teamRow?.players)
        ? (teamRow!.players as Player[])
        : [];

      // 4) Merge avec les joueurs déjà dans le match
      const fullRoster = mergeByNumber(teamPlayers, players);

      // 5) Coche ceux déjà actifs
      const activeNums = new Set(players.map((p) => p.number));
      const editable = fullRoster.map((p) => ({
        ...p,
        active: activeNums.has(p.number),
      })) as EditablePlayer[];

      setTempPlayers(editable);
    } catch (e) {
      console.error("openRosterEditor fatal error:", e);
      // Fallback minimal
      setTempPlayers(activePlayers.map((p) => ({ ...p, active: true })));
    } finally {
      setRosterLoading(false);
    }
  };

  const closeRosterEditor = () => {
    setShowRosterEditor(false);
    setTempPlayers([]);
  };

  const toggleTempPlayer = (num: number) => {
    setTempPlayers((prev) =>
      prev.map((p) => (p.number === num ? { ...p, active: !p.active } : p))
    );
  };

  const removeTempPlayer = (num: number) => {
    setTempPlayers((prev) => prev.filter((p) => p.number !== num));
  };

  const addTempPlayer = (p: Omit<Player, "photoUrl"> & { photoUrl?: string }) => {
    setTempPlayers((prev) => {
      if (prev.some((x) => x.number === p.number)) return prev; // évite doublon
      return [...prev, { ...p, active: true }];
    });
  };

  const saveRoster = async () => {
    const nextPlayers = tempPlayers
      .filter((p) => p.active)
      .map(({ active, ...rest }) => rest as Player);

    if (currentSide === "team" && match_id) {
      const { error } = await supabase.from("matches").update({ players: nextPlayers }).eq("id", match_id);
      if (error) {
        console.error("Erreur MAJ joueurs :", error);
        alert(
          tr("alerts.errorSavePlayers", {
            defaultValue: "Erreur lors de l’enregistrement des joueurs.",
          })
        );
        return;
      }
      setPlayers(nextPlayers);
      if (selectedPlayer && !nextPlayers.some((p) => p.number === selectedPlayer.number)) {
        setSelectedPlayer(null);
      }
    } else {
      // Adversaires : local seulement
      setOpponentPlayers(nextPlayers);
      if (
        selectedOpponentPlayer &&
        !nextPlayers.some((p) => p.number === selectedOpponentPlayer.number)
      ) {
        setSelectedOpponentPlayer(null);
      }
    }

    closeRosterEditor();
  };

  // Mini-formulaire d’ajout
  const [newPFirst, setNewPFirst] = useState("");
  const [newPLast, setNewPLast] = useState("");
  const [newPNumber, setNewPNumber] = useState<number | "">("");
  const [newPPos, setNewPPos] = useState("");

  const resetNewPForm = () => {
    setNewPFirst("");
    setNewPLast("");
    setNewPNumber("");
    setNewPPos("");
  };

  const handleQuickAdd = () => {
    const num = typeof newPNumber === "string" ? parseInt(newPNumber, 10) : newPNumber;
    if (!newPFirst || !newPLast || !num || !newPPos) {
      alert(
        tr("alerts.completeForm", {
          defaultValue: "Complète prénom, nom, numéro et poste.",
        })
      );
      return;
    }
    addTempPlayer({
      firstName: newPFirst.trim(),
      lastName: newPLast.trim(),
      number: num,
      position: newPPos.trim(),
      photoUrl: undefined,
    });
    resetNewPForm();
  };

  const updateMatchScore = async (t: number, o: number) => {
    if (!match_id) return;
    await supabase.from("matches").update({ score: `${t}-${o}` }).eq("id", match_id);
  };

  // Fetch match + stats
  useEffect(() => {
    const fetchMatchPlayers = async () => {
      const { data: match, error } = await supabase
        .from("matches")
        .select("players, score, team_id")
        .eq("id", match_id)
        .single();

      if (error || !match) {
        console.error("Erreur récupération match :", error);
        alert(
          tr("alerts.errorFetchMatch", {
            defaultValue: "Erreur lors de la récupération du match.",
          })
        );
        return;
      }
      if (match.players) setPlayers(match.players);

      if (typeof match.score === "string" && match.score.includes("-")) {
        const [t, o] = match.score.split("-").map((n: string) => parseInt(n, 10) || 0);
        setScore({ team: t, opponent: o });
      }
      setTeamId(match.team_id ?? null);
    };

    const fetchStats = async () => {
      const { data, error } = await supabase
        .from("match_stats")
        .select(
          "id, player_number, points, rebounds, assists, steals, blocks, turnovers, shots_attempted, shots_made, ft_attempted, ft_made, three_attempted, three_made, personal_fouls, is_opponent"
        )
        .eq("match_id", match_id);

      if (error) {
        console.error("Erreur récupération stats :", error);
        alert(
          tr("alerts.errorFetchStats", {
            defaultValue: "Erreur lors de la récupération des statistiques.",
          })
        );
        return;
      }

      const totals: Record<number, PlayerStatTotal> = {};
      const oppTotals: Record<number, PlayerStatTotal> = {};

      (data || []).forEach((row: any) => {
        const bucket = row.is_opponent ? oppTotals : totals;
        bucket[row.player_number] = {
          player_number: row.player_number,
          points: row.points || 0,
          rebounds: row.rebounds || 0,
          assists: row.assists || 0,
          steals: row.steals || 0,
          blocks: row.blocks || 0,
          turnovers: row.turnovers || 0,
          shots_attempted: row.shots_attempted || 0,
          shots_made: row.shots_made || 0,
          ft_attempted: row.ft_attempted || 0,
          ft_made: row.ft_made || 0,
          three_made: row.three_made || 0,
          three_attempted: row.three_attempted || 0,
          personal_fouls: row.personal_fouls || 0,
        };
      });

      setStats(totals);
      setOpponentStats(oppTotals);
    };

    fetchMatchPlayers();
    fetchStats();
  }, [match_id, tr]);

  // Lock scroll when modals are open (capacitor)
  useEffect(() => {
    const anyModalOpen = showRosterEditor || showCourt || showPlayerPicker;
    lockBodyScroll(anyModalOpen);
    return () => lockBodyScroll(false);
  }, [showRosterEditor, showCourt, showPlayerPicker, lockBodyScroll]);

  // Clôturer le match
  const handleCloseMatch = async () => {
    if (!match_id) return;

    // On ne prend que les joueurs qui ont des stats pour mon équipe
    const playerNumbers = Object.keys(stats).map(Number);
    const assignedPlayers = players.filter((p) => playerNumbers.includes(p.number));

    const { error: matchUpdateError } = await supabase
      .from("matches")
      .update({
        players: assignedPlayers,
        score: `${score.team}-${score.opponent}`,
        victory: score.team > score.opponent,
        live: false,
        is_live_finished: true,
      })
      .eq("id", match_id);

    if (matchUpdateError) {
      console.error("Erreur mise à jour du match :", matchUpdateError);
      alert(
        tr("alerts.errorCloseMatch", {
          defaultValue: "Erreur lors de la clôture du match",
        })
      );
      return;
    }

    const saveSideStats = async (side: Side, map: Record<number, PlayerStatTotal>) => {
      for (const stat of Object.values(map)) {
        const { data: existing, error } = await supabase
          .from("match_stats")
          .select("id")
          .eq("match_id", match_id)
          .eq("player_number", stat.player_number)
          .eq("is_opponent", side === "opponent")
          .maybeSingle();

        if (error) continue;

        const payload = { ...stat, is_opponent: side === "opponent" };

        if (existing?.id) {
          await supabase.from("match_stats").update(payload).eq("id", existing.id);
        } else {
          await supabase.from("match_stats").insert({
            match_id,
            ...payload,
          });
        }
      }
    };

    await saveSideStats("team", stats);
    await saveSideStats("opponent", opponentStats);

    alert(
      tr("alerts.successCloseMatch", {
        defaultValue: "✅ Match clôturé avec succès !",
      })
    );
    navigate(`/matchdetails/${match_id}`);
  };

  // --------- TIRS MON EQUIPE (avec terrain) ----------
  const handleCourtClick = async (e: React.MouseEvent<HTMLImageElement, MouseEvent>) => {
    if (currentSide !== "team") return;
    if (!selectedPlayer || !pendingShot) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;

    const { data: userData } = await supabase.auth.getUser();
    const user = userData.user;
    if (!user) return;

    const { data: inserted, error: insertErr } = await supabase
      .from("shot_locations")
      .insert({
        match_id,
        player_number: selectedPlayer.number,
        player_name: `${selectedPlayer.firstName} ${selectedPlayer.lastName}`,
        x,
        y,
        made: pendingShot.made,
        user_id: user.id,
      })
      .select("id")
      .single();

    if (insertErr) {
      console.error("Erreur insertion tir :", insertErr);
      return;
    }

    setActionHistory((prev) => [
      ...prev,
      {
        side: "team",
        type: "shot",
        player_number: selectedPlayer.number,
        value: pendingShot.value,
        made: pendingShot.made,
        shot_id: inserted?.id,
      },
    ]);

    const { data } = await supabase
      .from("match_stats")
      .select("id, points, shots_attempted, shots_made, ft_made, ft_attempted, three_made, three_attempted")
      .eq("match_id", match_id)
      .eq("player_number", selectedPlayer.number)
      .eq("is_opponent", false)
      .maybeSingle();

    const row = data as { id: string } & Partial<PlayerStatTotal>;
    const updates: Record<string, number> = {
      shots_attempted: (row?.shots_attempted || 0) + 1,
    };

    if (pendingShot.value === 3) {
      updates.three_attempted = (row?.three_attempted || 0) + 1;
      if (pendingShot.made) {
        updates.three_made = (row?.three_made || 0) + 1;
        updates.shots_made = (row?.shots_made || 0) + 1;
        updates.points = (row?.points || 0) + 3;
      }
    } else if (pendingShot.made) {
      updates.shots_made = (row?.shots_made || 0) + 1;
      updates.points = (row?.points || 0) + pendingShot.value;
    }

    if (row?.id) {
      await supabase.from("match_stats").update(updates).eq("id", row.id);
    } else {
      await supabase.from("match_stats").insert({
        match_id,
        player_number: selectedPlayer.number,
        player_name: `${selectedPlayer.firstName} ${selectedPlayer.lastName}`,
        position: selectedPlayer.position,
        is_opponent: false,
        ...updates,
      });
    }

    setStats((prev) => {
      const current =
        prev[selectedPlayer.number] || {
          player_number: selectedPlayer.number,
          points: 0,
          rebounds: 0,
          assists: 0,
          steals: 0,
          blocks: 0,
          turnovers: 0,
          shots_attempted: 0,
          shots_made: 0,
          ft_attempted: 0,
          ft_made: 0,
          three_attempted: 0,
          three_made: 0,
          personal_fouls: 0,
        };

      return {
        ...prev,
        [selectedPlayer.number]: {
          ...current,
          shots_attempted: current.shots_attempted + 1,
          shots_made: current.shots_made + (pendingShot.made ? 1 : 0),
          points: current.points + (pendingShot.made ? pendingShot.value : 0),
          three_attempted: current.three_attempted + (pendingShot.value === 3 ? 1 : 0),
          three_made: current.three_made + (pendingShot.value === 3 && pendingShot.made ? 1 : 0),
        },
      };
    });

    if (pendingShot.made) {
      setScore((prev) => {
        const next = { ...prev, team: prev.team + pendingShot.value };
        void updateMatchScore(next.team, next.opponent);
        return next;
      });
    }

    setFeedbackShot({ x, y, made: pendingShot.made });
    setTimeout(() => setFeedbackShot(null), 900);
    setShowCourt(false);
    setPendingShot(null);
  };

  // --------- TIRS ADVERSAIRES (sans terrain) ----------
  const handleOpponentShot = async (
    { made, value }: { made: boolean; value: 2 | 3 },
    playerNumber?: number
  ) => {
    const targetNumber = playerNumber !== undefined ? playerNumber : OPP_TEAM_PLAYER_NUMBER;

    const player =
      playerNumber !== undefined
        ? opponentPlayers.find((p) => p.number === playerNumber) ?? null
        : null;

    const playerName = player
      ? `${player.firstName} ${player.lastName}`
      : tr("labels.opponentTeam", { defaultValue: "Équipe adverse" });
    const position = player?.position ?? "";

    setActionHistory((prev) => [
      ...prev,
      {
        side: "opponent",
        type: "shot",
        player_number: targetNumber,
        value,
        made,
      },
    ]);

    const { data } = await supabase
      .from("match_stats")
      .select("id, points, shots_attempted, shots_made, three_attempted, three_made")
      .eq("match_id", match_id)
      .eq("player_number", targetNumber)
      .eq("is_opponent", true)
      .maybeSingle();

    const row = data as { id: string } & Partial<PlayerStatTotal>;

    const updates: Record<string, number> = {
      shots_attempted: (row?.shots_attempted || 0) + 1,
    };

    if (value === 3) {
      updates.three_attempted = (row?.three_attempted || 0) + 1;
      if (made) {
        updates.three_made = (row?.three_made || 0) + 1;
        updates.shots_made = (row?.shots_made || 0) + 1;
        updates.points = (row?.points || 0) + 3;
      }
    } else {
      if (made) {
        updates.shots_made = (row?.shots_made || 0) + 1;
        updates.points = (row?.points || 0) + 2;
      }
    }

    if (row?.id) {
      await supabase.from("match_stats").update(updates).eq("id", row.id);
    } else {
      await supabase.from("match_stats").insert({
        match_id,
        player_number: targetNumber,
        player_name: playerName,
        position,
        is_opponent: true,
        ...updates,
      });
    }

    setOpponentStats((prev) => {
      const current =
        prev[targetNumber] || {
          player_number: targetNumber,
          points: 0,
          rebounds: 0,
          assists: 0,
          steals: 0,
          blocks: 0,
          turnovers: 0,
          shots_attempted: 0,
          shots_made: 0,
          ft_attempted: 0,
          ft_made: 0,
          three_attempted: 0,
          three_made: 0,
          personal_fouls: 0,
        };
      return {
        ...prev,
        [targetNumber]: {
          ...current,
          shots_attempted: current.shots_attempted + 1,
          shots_made: current.shots_made + (made ? 1 : 0),
          points: current.points + (made ? value : 0),
          three_attempted: current.three_attempted + (value === 3 ? 1 : 0),
          three_made: current.three_made + (value === 3 && made ? 1 : 0),
        },
      };
    });

    if (made) {
      setScore((prev) => {
        const next = { ...prev, opponent: prev.opponent + value };
        void updateMatchScore(next.team, next.opponent);
        return next;
      });
    }
  };

  // --------- UNDO ----------
  const applyUndoOn = async (target: UndoAction) => {
    if (target.type === "shot" && (target as any).shot_id) {
      try {
        await supabase.from("shot_locations").delete().eq("id", (target as any).shot_id);
      } catch (e) {
        console.error("Erreur suppression shot_locations :", e);
      }
    }

    const isOpp = target.side === "opponent";

    const { data, error } = await supabase
      .from("match_stats")
      .select("*")
      .eq("match_id", match_id)
      .eq("player_number", target.player_number)
      .eq("is_opponent", isOpp)
      .maybeSingle();

    if (error || !data) return;

    const updates: Record<string, number> = {};
    if (target.type === "stat") {
      updates[target.stat as string] = Math.max((data[target.stat as string] || 0) - 1, 0);
    } else {
      updates.shots_attempted = Math.max((data.shots_attempted || 0) - 1, 0);

      if (target.value === 3) {
        updates.three_attempted = Math.max((data.three_attempted || 0) - 1, 0);
        if (target.made) {
          updates.shots_made = Math.max((data.shots_made || 0) - 1, 0);
          updates.three_made = Math.max((data.three_made || 0) - 1, 0);
          updates.points = Math.max((data.points || 0) - 3, 0);
        }
      } else if (target.value === 2) {
        if (target.made) {
          updates.shots_made = Math.max((data.shots_made || 0) - 1, 0);
          updates.points = Math.max((data.points || 0) - 2, 0);
        }
      } else if (target.value === 1) {
        updates.ft_attempted = Math.max((data.ft_attempted || 0) - 1, 0);
        if (target.made) {
          updates.ft_made = Math.max((data.ft_made || 0) - 1, 0);
          updates.points = Math.max((data.points || 0) - 1, 0);
        }
      }
    }

    await supabase.from("match_stats").update(updates).eq("id", data.id);

    const updateLocal = (map: Record<number, PlayerStatTotal>) => {
      const playerStats = map[target.player_number];
      if (!playerStats) return map;
      const next = { ...playerStats };

      if (target.type === "stat") {
        const k = target.stat as keyof PlayerStatTotal;
        next[k] = Math.max((next[k] || 0) - 1, 0);
      } else {
        next.shots_attempted = Math.max((next.shots_attempted || 0) - 1, 0);

        if (target.value === 3) {
          next.three_attempted = Math.max((next.three_attempted || 0) - 1, 0);
          if (target.made) {
            next.shots_made = Math.max((next.shots_made || 0) - 1, 0);
            next.three_made = Math.max((next.three_made || 0) - 1, 0);
            next.points = Math.max((next.points || 0) - 3, 0);
          }
        } else if (target.value === 2) {
          if (target.made) {
            next.shots_made = Math.max((next.shots_made || 0) - 1, 0);
            next.points = Math.max((next.points || 0) - 2, 0);
          }
        } else if (target.value === 1) {
          next.ft_attempted = Math.max((next.ft_attempted || 0) - 1, 0);
          if (target.made) {
            next.ft_made = Math.max((next.ft_made || 0) - 1, 0);
            next.points = Math.max((next.points || 0) - 1, 0);
          }
        }
      }

      return { ...map, [target.player_number]: next };
    };

    if (target.side === "team") setStats((prev) => updateLocal(prev));
    else setOpponentStats((prev) => updateLocal(prev));

    if (target.type === "shot" && target.made) {
      const delta = target.value;
      setScore((prev) => {
        const next =
          target.side === "team"
            ? { ...prev, team: Math.max(prev.team - delta, 0) }
            : { ...prev, opponent: Math.max(prev.opponent - delta, 0) };

        void updateMatchScore(next.team, next.opponent);
        return next;
      });
    }
  };

  const handleUndo = async () => {
    const last = actionHistory[actionHistory.length - 1];
    if (!last) return;
    await applyUndoOn(last);
    setActionHistory((prev) => prev.slice(0, -1));
  };

  const handleUndoAt = async (idx: number) => {
    const target = actionHistory[idx];
    if (!target) return;
    await applyUndoOn(target);
    setActionHistory((prev) => prev.filter((_, i) => i !== idx));
  };

  // --------- +1 STAT (équipe + adversaire) ----------
  const handleAddStat = async (type: StatType) => {
    if (currentSide === "team" && !selectedPlayer) {
      return alert(tr("alerts.choosePlayer", { defaultValue: "Sélectionnez un joueur" }));
    }

    const isOpp = currentSide === "opponent";
    const player =
      currentSide === "team" ? selectedPlayer! : selectedOpponentPlayer ?? null;

    const playerNumber = player ? player.number : OPP_TEAM_PLAYER_NUMBER;
    const playerName = player
      ? `${player.firstName} ${player.lastName}`
      : tr("labels.opponentTeam", { defaultValue: "Équipe adverse" });
    const position = player?.position ?? "";

    const { data, error } = await supabase
      .from("match_stats")
      .select(
        "id, points, rebounds, assists, steals, blocks, turnovers, shots_attempted, shots_made, personal_fouls"
      )
      .eq("match_id", match_id)
      .eq("player_number", playerNumber)
      .eq("is_opponent", isOpp)
      .maybeSingle();

    setActionHistory((prev) => [
      ...prev,
      { side: currentSide, type: "stat", player_number: playerNumber, stat: type },
    ]);

    if (error) {
      console.error("Erreur récupération ligne stat:", error);
      alert(tr("alerts.errorFetchStatLine", { defaultValue: "Erreur récupération ligne stat" }));
      return;
    }

    const row = data as { id: string } & Partial<PlayerStatTotal>;
    const updatedValue = (row?.[type] || 0) + 1;

    if (row?.id) {
      await supabase.from("match_stats").update({ [type]: updatedValue }).eq("id", row.id);
    } else {
      await supabase.from("match_stats").insert({
        match_id,
        player_number: playerNumber,
        player_name: playerName,
        position,
        is_opponent: isOpp,
        [type]: 1,
      });
    }

    const updateLocal = (map: Record<number, PlayerStatTotal>) => {
      const current =
        map[playerNumber] || {
          player_number: playerNumber,
          points: 0,
          rebounds: 0,
          assists: 0,
          steals: 0,
          blocks: 0,
          turnovers: 0,
          shots_attempted: 0,
          shots_made: 0,
          ft_attempted: 0,
          ft_made: 0,
          three_attempted: 0,
          three_made: 0,
          personal_fouls: 0,
        };
      return {
        ...map,
        [playerNumber]: {
          ...current,
          [type]: (current[type as keyof PlayerStatTotal] as number) + 1,
        },
      };
    };

    if (currentSide === "team") setStats((prev) => updateLocal(prev));
    else setOpponentStats((prev) => updateLocal(prev));
  };

  const handleShotButton = (made: boolean, value: 1 | 2 | 3) => {
    if (currentSide === "team") {
      if (!selectedPlayer) {
        return alert(tr("alerts.choosePlayer", { defaultValue: "Sélectionnez un joueur" }));
      }
      const shot = { made, value };
      setPendingShot(shot);

      if (value === 2 || value === 3) setShowCourt(true);
      else handleFreeThrow({ made, value: 1 }, "team");
      return;
    }

    // adversaires
    const hasOpponentPlayer = !!selectedOpponentPlayer;
    if (value === 1) {
      handleFreeThrow({ made, value: 1 }, "opponent");
    } else {
      const playerNumber = hasOpponentPlayer ? selectedOpponentPlayer!.number : undefined;
      handleOpponentShot({ made, value: value as 2 | 3 }, playerNumber);
    }
  };

  const handleFreeThrow = async ({ made }: { made: boolean; value: 1 }, side: Side) => {
    const isOpp = side === "opponent";

    let player: Player | null;
    if (side === "team") player = selectedPlayer;
    else player = selectedOpponentPlayer;

    const playerNumber = player ? player.number : OPP_TEAM_PLAYER_NUMBER;
    const playerName = player
      ? `${player.firstName} ${player.lastName}`
      : tr("labels.opponentTeam", { defaultValue: "Équipe adverse" });
    const position = player?.position ?? "";

    if (side === "team" && !player) return;

    setActionHistory((prev) => [
      ...prev,
      { side, type: "shot", player_number: playerNumber, value: 1, made },
    ]);

    const { data } = await supabase
      .from("match_stats")
      .select("id, points, ft_attempted, ft_made")
      .eq("match_id", match_id)
      .eq("player_number", playerNumber)
      .eq("is_opponent", isOpp)
      .maybeSingle();

    const row = data as { id: string } & Partial<PlayerStatTotal>;
    const updates: Record<string, number> = {
      ft_attempted: (row?.ft_attempted || 0) + 1,
    };
    if (made) {
      updates.ft_made = (row?.ft_made || 0) + 1;
      updates.points = (row?.points || 0) + 1;
    }

    if (row?.id) {
      await supabase.from("match_stats").update(updates).eq("id", row.id);
    } else {
      await supabase.from("match_stats").insert({
        match_id,
        player_number: playerNumber,
        player_name: playerName,
        position,
        is_opponent: isOpp,
        ...updates,
      });
    }

    const updateLocal = (map: Record<number, PlayerStatTotal>) => {
      const current =
        map[playerNumber] || {
          player_number: playerNumber,
          points: 0,
          rebounds: 0,
          assists: 0,
          steals: 0,
          blocks: 0,
          turnovers: 0,
          shots_attempted: 0,
          shots_made: 0,
          ft_attempted: 0,
          ft_made: 0,
          three_attempted: 0,
          three_made: 0,
          personal_fouls: 0,
        };
      return {
        ...map,
        [playerNumber]: {
          ...current,
          ft_attempted: current.ft_attempted + 1,
          ft_made: current.ft_made + (made ? 1 : 0),
          points: current.points + (made ? 1 : 0),
        },
      };
    };

    if (side === "team") setStats((prev) => updateLocal(prev));
    else setOpponentStats((prev) => updateLocal(prev));

    if (made) {
      setScore((prev) => {
        const next = side === "team" ? { ...prev, team: prev.team + 1 } : { ...prev, opponent: prev.opponent + 1 };
        void updateMatchScore(next.team, next.opponent);
        return next;
      });
    }
  };

  // Choix du joueur dans la modale : on applique la sélection puis on déclenche
  // l'action en attente (tir ou stat) une fois le state réellement à jour
  // (dispatchTick force l'effet ci-dessous à s'exécuter même si le joueur
  // sélectionné ne change pas de valeur, ex: "Équipe adverse" -> "Équipe adverse").
  const pickPlayerAndDispatch = (p: Player | null) => {
    setActiveSelectedPlayer(p);
    setShowPlayerPicker(false);
    setDispatchTick((t) => t + 1);
  };

  useEffect(() => {
    if (dispatchTick === 0 || !pendingAction) return;
    const action = pendingAction;
    setPendingAction(null);
    if (action.kind === "shot") handleShotButton(action.made, action.value);
    else handleAddStat(action.type);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatchTick]);

  const safeNum = (v: string) => {
    const n = parseInt(v, 10);
    return Number.isFinite(n) && n >= 0 ? n : 0;
  };

  const FALLBACK_STAT_LABELS: Record<StatType, string> = {
    points: "Point",
    rebounds: "Rebond",
    assists: "Passe",
    steals: "Interception",
    blocks: "Contre",
    turnovers: "Perte",
    personal_fouls: "Faute perso",
    shots_attempted: "Tir tenté",
    shots_made: "Tir marqué",
  };

  const statLabel = (k: StatType) =>
    tr(`stats.labels.${k}`, { defaultValue: FALLBACK_STAT_LABELS[k] });

  const playerLabel = (num: number, side: Side) => {
    const list = side === "team" ? players : opponentPlayers;
    if (side === "opponent" && num === OPP_TEAM_PLAYER_NUMBER) {
      return tr("labels.opponentTeam", { defaultValue: "Équipe adverse" });
    }
    const p = list.find((pl) => pl.number === num);
    return p ? `#${p.number} ${p.firstName} ${p.lastName}` : `#${num}`;
  };

  const formatLastAction = (a: UndoAction) => {
    if (a.type === "stat") {
      return tr("history.format.stat", {
        player: playerLabel(a.player_number, a.side),
        label: statLabel(a.stat as StatType),
        defaultValue: "{{player}} · +1 {{label}}",
      });
    }
    const kind =
      a.value === 1
        ? tr("history.kind.ft", { defaultValue: "LF" })
        : tr("history.kind.points", { count: a.value, defaultValue: "{{count}} pts" });
    const res = a.made
      ? tr("history.result.made", { defaultValue: "marqué" })
      : tr("history.result.missed", { defaultValue: "manqué" });

    return tr("history.format.shot", {
      player: playerLabel(a.player_number, a.side),
      kind,
      result: res,
      defaultValue: "{{player}} · {{kind}} {{result}}",
    });
  };

  // ===== RENDER =====
  return (
    <section
      className="
        min-h-[100dvh] w-full
        bg-gray-50 text-gray-900 dark:bg-gray-950 dark:text-gray-100
      "
      style={{
        paddingTop: "env(safe-area-inset-top)",
        paddingBottom: "env(safe-area-inset-bottom)",
      }}
    >
      {/* Header sticky (mobile friendly) */}
      <div className="sticky top-0 z-40 bg-gray-50/90 dark:bg-gray-950/90 backdrop-blur border-b border-gray-200 dark:border-gray-800">
        <div className="max-w-4xl mx-auto px-3 py-3 flex items-center justify-between gap-2">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-800 bg-white/80 dark:bg-gray-900/60 active:scale-[0.98]"
            aria-label={tr("actions.back", { defaultValue: "Retour" })}
            title={tr("actions.back", { defaultValue: "Retour" })}
          >
            <ArrowLeft size={18} />
            <span className="text-sm font-medium">{tr("actions.back", { defaultValue: "Retour" })}</span>
          </button>

          <div className="min-w-0 text-center flex-1">
            <h1 className="text-base sm:text-lg font-bold text-orange-600 dark:text-orange-400 truncate">
              {tr("header.title", { defaultValue: "Match en Live" })}
            </h1>
            <div className="text-[11px] opacity-70">
              {currentSide === "team"
                ? tr("toggle.team", { defaultValue: "Mon équipe" })
                : tr("toggle.opponent", { defaultValue: "Adversaires" })}
              {" · "}
              {score.team}-{score.opponent}
            </div>
          </div>

          <button
            onClick={handleCloseMatch}
            className="px-3 py-2 rounded-xl bg-black text-white active:scale-[0.98] hover:bg-gray-800 text-sm font-semibold"
            title={tr("actions.closeMatch", { defaultValue: "Clôturer le match" })}
          >
            {tr("actions.closeMatchShort", { defaultValue: "Clôturer" })}
          </button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-3 pb-24 pt-4">
        {/* Toggle équipe / adversaires */}
        <div className="flex justify-center mb-4">
          <div className="inline-flex rounded-2xl border border-gray-300 bg-white p-1 dark:bg-gray-900 dark:border-gray-700 w-full max-w-md">
            <button
              className={`flex-1 px-3 py-2 text-sm rounded-2xl font-medium ${
                currentSide === "team" ? "bg-orange-600 text-white" : "text-gray-700 dark:text-gray-200"
              }`}
              onClick={() => setCurrentSide("team")}
            >
              {tr("toggle.team", { defaultValue: "Mon équipe" })}
            </button>
            <button
              className={`flex-1 px-3 py-2 text-sm rounded-2xl font-medium ${
                currentSide === "opponent" ? "bg-orange-600 text-white" : "text-gray-700 dark:text-gray-200"
              }`}
              onClick={() => setCurrentSide("opponent")}
            >
              {tr("toggle.opponent", { defaultValue: "Adversaires" })}
            </button>
          </div>
        </div>

        {/* Scoreboard (touch-friendly inputs) */}
        <div className="grid grid-cols-3 gap-2 mb-5 text-center rounded-2xl border bg-white border-gray-200 shadow-sm p-3 dark:bg-gray-900 dark:border-gray-800">
          <div className="flex flex-col items-center gap-1">
            <label className="text-xs text-gray-700 dark:text-gray-300">
              {tr("scoreboard.home", { defaultValue: "🏠 Mon équipe" })}
            </label>
            <input
              type="number"
              inputMode="numeric"
              pattern="[0-9]*"
              value={score.team}
              onChange={(e) => setScore({ ...score, team: safeNum(e.target.value) })}
              onBlur={() => updateMatchScore(score.team, score.opponent)}
              className="w-24 h-11 text-center rounded-xl border bg-white border-gray-300 dark:bg-gray-800 dark:border-gray-700 text-base"
              min={0}
            />
          </div>

          <div className="flex items-center justify-center text-lg">🆚</div>

          <div className="flex flex-col items-center gap-1">
            <label className="text-xs text-gray-700 dark:text-gray-300">
              {tr("scoreboard.away", { defaultValue: "Adversaire" })}
            </label>
            <input
              type="number"
              inputMode="numeric"
              pattern="[0-9]*"
              value={score.opponent}
              onChange={(e) => setScore({ ...score, opponent: safeNum(e.target.value) })}
              onBlur={() => updateMatchScore(score.team, score.opponent)}
              className="w-24 h-11 text-center rounded-xl border bg-white border-gray-300 dark:bg-gray-800 dark:border-gray-700 text-base"
              min={0}
            />
          </div>
        </div>

        {/* Tabs (wrap on mobile) */}
        <div className="flex flex-wrap justify-center mb-4 gap-2">
          <button
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border ${
              activeTab === "live"
                ? "bg-orange-600 text-white border-orange-600"
                : "bg-white text-gray-800 border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-700 dark:hover:bg-gray-700"
            }`}
            onClick={() => setActiveTab("live")}
          >
            <PlayCircle size={16} /> {tr("tabs.live", { defaultValue: "Live" })}
          </button>

          <button
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border ${
              activeTab === "stats"
                ? "bg-orange-600 text-white border-orange-600"
                : "bg-white text-gray-800 border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-700 dark:hover:bg-gray-700"
            }`}
            onClick={() => setActiveTab("stats")}
          >
            <BarChart2 size={16} />{" "}
            {currentSide === "team"
              ? tr("tabs.statsTeam", { defaultValue: "Stats équipe" })
              : tr("tabs.statsOpp", { defaultValue: "Stats adversaires" })}
          </button>

          <button
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border ${
              activeTab === "history"
                ? "bg-orange-600 text-white border-orange-600"
                : "bg-white text-gray-800 border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-700 dark:hover:bg-gray-700"
            }`}
            onClick={() => setActiveTab("history")}
          >
            <Activity size={16} /> {tr("tabs.history", { defaultValue: "Historique" })}
          </button>
        </div>

        {/* Live panel */}
        {activeTab === "live" && (
          <>
            <div className="flex items-center justify-between mb-2 gap-2">
              <h2 className="font-semibold text-sm sm:text-base">
                {tr("live.actionsTitle", { defaultValue: "Ajouter une statistique :" })}
              </h2>

              <button
                onClick={openRosterEditor}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-gray-300 bg-white hover:bg-gray-50 text-sm dark:bg-gray-800 dark:border-gray-700 dark:hover:bg-gray-700 active:scale-[0.98]"
                title={tr("live.editRosterTitle", { defaultValue: "Éditer les joueurs présents" })}
              >
                <Pencil size={16} />
                <span className="hidden xs:inline">{tr("live.editRoster", { defaultValue: "Éditer joueurs" })}</span>
                <span className="xs:hidden">{tr("live.editRosterShort", { defaultValue: "Roster" })}</span>
              </button>
            </div>

            <div className="space-y-4">
              <div className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-3">
                <h3 className="font-medium text-sm sm:text-base">
                  {tr("live.shotsSectionTitle", { defaultValue: "Tirs" })}
                </h3>

                <div className="grid grid-cols-2 gap-2 mt-3">
                  <button
                    onClick={() => openPlayerPicker({ kind: "shot", made: true, value: 2 })}
                    className="bg-green-600 hover:bg-green-700 text-white p-3 rounded-xl text-sm font-semibold active:scale-[0.98]"
                  >
                    {tr("live.shots.made2", { defaultValue: "✅ 2 pts marqué" })}
                  </button>
                  <button
                    onClick={() => openPlayerPicker({ kind: "shot", made: true, value: 3 })}
                    className="bg-green-600 hover:bg-green-700 text-white p-3 rounded-xl text-sm font-semibold active:scale-[0.98]"
                  >
                    {tr("live.shots.made3", { defaultValue: "✅ 3 pts marqué" })}
                  </button>
                  <button
                    onClick={() => openPlayerPicker({ kind: "shot", made: true, value: 1 })}
                    className="bg-green-600 hover:bg-green-700 text-white p-3 rounded-xl text-sm font-semibold active:scale-[0.98]"
                  >
                    {tr("live.shots.madeFT", { defaultValue: "✅ LF marqué" })}
                  </button>
                  <button
                    onClick={() => openPlayerPicker({ kind: "shot", made: false, value: 2 })}
                    className="bg-gray-200 hover:bg-gray-300 text-gray-900 p-3 rounded-xl text-sm font-semibold dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-gray-100 active:scale-[0.98]"
                  >
                    {tr("live.shots.miss2", { defaultValue: "❌ 2 pts manqué" })}
                  </button>
                  <button
                    onClick={() => openPlayerPicker({ kind: "shot", made: false, value: 3 })}
                    className="bg-gray-200 hover:bg-gray-300 text-gray-900 p-3 rounded-xl text-sm font-semibold dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-gray-100 active:scale-[0.98]"
                  >
                    {tr("live.shots.miss3", { defaultValue: "❌ 3 pts manqué" })}
                  </button>
                  <button
                    onClick={() => openPlayerPicker({ kind: "shot", made: false, value: 1 })}
                    className="bg-gray-200 hover:bg-gray-300 text-gray-900 p-3 rounded-xl text-sm font-semibold dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-gray-100 active:scale-[0.98]"
                  >
                    {tr("live.shots.missFT", { defaultValue: "❌ LF manqué" })}
                  </button>
                </div>
              </div>

              <div className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-3">
                <h3 className="font-medium text-sm sm:text-base">
                  {tr("live.otherStatsSectionTitle", { defaultValue: "Autres stats" })}
                </h3>

                <div className="grid grid-cols-2 gap-2 mt-3">
                  <button
                    className="bg-blue-600 hover:bg-blue-700 text-white p-3 rounded-xl text-sm font-semibold active:scale-[0.98]"
                    onClick={() => openPlayerPicker({ kind: "stat", type: "rebounds" })}
                  >
                    {tr("live.otherStats.rebound", { defaultValue: "+1 Rebond" })}
                  </button>
                  <button
                    className="bg-purple-600 hover:bg-purple-700 text-white p-3 rounded-xl text-sm font-semibold active:scale-[0.98]"
                    onClick={() => openPlayerPicker({ kind: "stat", type: "assists" })}
                  >
                    {tr("live.otherStats.assist", { defaultValue: "+1 Passe" })}
                  </button>
                  <button
                    className="bg-yellow-500 hover:bg-yellow-600 text-black p-3 rounded-xl text-sm font-semibold active:scale-[0.98]"
                    onClick={() => openPlayerPicker({ kind: "stat", type: "steals" })}
                  >
                    {tr("live.otherStats.steal", { defaultValue: "+1 Interception" })}
                  </button>
                  <button
                    className="bg-gray-700 hover:bg-gray-600 text-white p-3 rounded-xl text-sm font-semibold active:scale-[0.98]"
                    onClick={() => openPlayerPicker({ kind: "stat", type: "blocks" })}
                  >
                    {tr("live.otherStats.block", { defaultValue: "+1 Contre" })}
                  </button>
                  <button
                    className="bg-red-600 hover:bg-red-700 text-white p-3 rounded-xl text-sm font-semibold active:scale-[0.98]"
                    onClick={() => openPlayerPicker({ kind: "stat", type: "turnovers" })}
                  >
                    {tr("live.otherStats.turnover", { defaultValue: "+1 Perte" })}
                  </button>
                  <button
                    className="bg-orange-600 hover:bg-orange-700 text-white p-3 rounded-xl text-sm font-semibold active:scale-[0.98]"
                    onClick={() => openPlayerPicker({ kind: "stat", type: "personal_fouls" })}
                  >
                    {tr("live.otherStats.foul", { defaultValue: "+1 Faute perso" })}
                  </button>
                </div>
              </div>

              {/* Dernière action + Annuler */}
              {actionHistory.length > 0 && (() => {
                const last = actionHistory[actionHistory.length - 1];
                return (
                  <div className="rounded-2xl border bg-white border-gray-200 p-3 dark:bg-gray-900 dark:border-gray-800">
                    <div className="text-sm">
                      <span className="inline-block px-2 py-0.5 rounded-full text-xs font-semibold bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300 mr-2">
                        {tr("lastAction.label", { defaultValue: "Dernière action" })}
                      </span>
                      <span className="font-medium">{formatLastAction(last)}</span>
                    </div>
                    <div className="mt-3 flex justify-end">
                      <button
                        onClick={handleUndo}
                        className="bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-4 rounded-xl shadow active:scale-[0.98]"
                        title={tr("lastAction.undoTitle", { defaultValue: "Annuler la dernière action" })}
                      >
                        ↩️ {tr("lastAction.undoButton", { defaultValue: "Annuler" })}
                      </button>
                    </div>
                  </div>
                );
              })()}
            </div>
          </>
        )}

        {/* Stats panel */}
        {activeTab === "stats" && (
          <div className="overflow-x-auto rounded-2xl border bg-white border-gray-200 shadow-sm p-3 dark:bg-gray-900 dark:border-gray-800">
            <table className="min-w-[860px] w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  {[
                    tr("stats.table.number", { defaultValue: "#" }),
                    tr("stats.table.name", { defaultValue: "Nom" }),
                    tr("stats.table.pts", { defaultValue: "Pts" }),
                    tr("stats.table.reb", { defaultValue: "Reb" }),
                    tr("stats.table.ast", { defaultValue: "Pad" }),
                    tr("stats.table.stl", { defaultValue: "Int" }),
                    tr("stats.table.blk", { defaultValue: "Ctr" }),
                    tr("stats.table.tov", { defaultValue: "Per" }),
                    tr("stats.table.pf", { defaultValue: "FP" }),
                    tr("stats.table.fg", { defaultValue: "Tirs" }),
                    tr("stats.table.ft", { defaultValue: "LF" }),
                    tr("stats.table.tp", { defaultValue: "3pts" }),
                  ].map((h) => (
                    <th
                      key={h}
                      className="px-2 py-2 text-left border-b border-gray-200 dark:border-gray-700 whitespace-nowrap"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {activePlayers.map((p) => {
                  const def: PlayerStatTotal = {
                    player_number: p.number,
                    points: 0,
                    rebounds: 0,
                    assists: 0,
                    steals: 0,
                    blocks: 0,
                    turnovers: 0,
                    shots_attempted: 0,
                    shots_made: 0,
                    ft_attempted: 0,
                    ft_made: 0,
                    three_made: 0,
                    three_attempted: 0,
                    personal_fouls: 0,
                  };
                  const s = { ...def, ...(activeStats[p.number] || {}) };

                  return (
                    <tr key={p.number} className="hover:bg-gray-50 dark:hover:bg-gray-800/60">
                      <td className="px-2 py-2 border-b border-gray-100 dark:border-gray-800 text-center">
                        {p.number}
                      </td>
                      <td className="px-2 py-2 border-b border-gray-100 dark:border-gray-800 whitespace-nowrap">
                        {p.firstName} {p.lastName}
                      </td>
                      <td className="px-2 py-2 border-b border-gray-100 dark:border-gray-800 text-center">
                        {s.points}
                      </td>
                      <td className="px-2 py-2 border-b border-gray-100 dark:border-gray-800 text-center">
                        {s.rebounds}
                      </td>
                      <td className="px-2 py-2 border-b border-gray-100 dark:border-gray-800 text-center">
                        {s.assists}
                      </td>
                      <td className="px-2 py-2 border-b border-gray-100 dark:border-gray-800 text-center">
                        {s.steals}
                      </td>
                      <td className="px-2 py-2 border-b border-gray-100 dark:border-gray-800 text-center">
                        {s.blocks}
                      </td>
                      <td className="px-2 py-2 border-b border-gray-100 dark:border-gray-800 text-center">
                        {s.turnovers}
                      </td>
                      <td className="px-2 py-2 border-b border-gray-100 dark:border-gray-800 text-center">
                        {s.personal_fouls}
                      </td>
                      <td className="px-2 py-2 border-b border-gray-100 dark:border-gray-800 text-center whitespace-nowrap">
                        {s.shots_made}/{s.shots_attempted}
                      </td>
                      <td className="px-2 py-2 border-b border-gray-100 dark:border-gray-800 text-center whitespace-nowrap">
                        {s.ft_made}/{s.ft_attempted}
                      </td>
                      <td className="px-2 py-2 border-b border-gray-100 dark:border-gray-800 text-center whitespace-nowrap">
                        {s.three_made}/{s.three_attempted}
                      </td>
                    </tr>
                  );
                })}

                <tr className="bg-orange-50 dark:bg-orange-900/20 font-semibold">
                  <td className="px-2 py-2 text-center border-t border-orange-200 dark:border-orange-900/30" colSpan={2}>
                    {currentSide === "team"
                      ? tr("stats.table.teamRow", { defaultValue: "Équipe" })
                      : tr("stats.table.oppRow", { defaultValue: "Adversaire" })}
                  </td>
                  <td className="px-2 py-2 text-center border-t border-orange-200 dark:border-orange-900/30">
                    {sum("points", currentSide)}
                  </td>
                  <td className="px-2 py-2 text-center border-t border-orange-200 dark:border-orange-900/30">
                    {sum("rebounds", currentSide)}
                  </td>
                  <td className="px-2 py-2 text-center border-t border-orange-200 dark:border-orange-900/30">
                    {sum("assists", currentSide)}
                  </td>
                  <td className="px-2 py-2 text-center border-t border-orange-200 dark:border-orange-900/30">
                    {sum("steals", currentSide)}
                  </td>
                  <td className="px-2 py-2 text-center border-t border-orange-200 dark:border-orange-900/30">
                    {sum("blocks", currentSide)}
                  </td>
                  <td className="px-2 py-2 text-center border-t border-orange-200 dark:border-orange-900/30">
                    {sum("turnovers", currentSide)}
                  </td>
                  <td className="px-2 py-2 text-center border-t border-orange-200 dark:border-orange-900/30">
                    {sum("personal_fouls", currentSide)}
                  </td>
                  <td className="px-2 py-2 text-center border-t border-orange-200 dark:border-orange-900/30 whitespace-nowrap">
                    {sum("shots_made", currentSide)}/{sum("shots_attempted", currentSide)}
                  </td>
                  <td className="px-2 py-2 text-center border-t border-orange-200 dark:border-orange-900/30 whitespace-nowrap">
                    {sum("ft_made", currentSide)}/{sum("ft_attempted", currentSide)}
                  </td>
                  <td className="px-2 py-2 text-center border-t border-orange-200 dark:border-orange-900/30 whitespace-nowrap">
                    {sum("three_made", currentSide)}/{sum("three_attempted", currentSide)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {/* Historique panel */}
        {activeTab === "history" && (
          <div className="overflow-x-auto rounded-2xl border bg-white border-gray-200 shadow-sm p-3 dark:bg-gray-900 dark:border-gray-800">
            {actionHistory.length === 0 ? (
              <p className="text-center text-sm opacity-70">
                {tr("history.empty", { defaultValue: "Aucune action pour l’instant" })}
              </p>
            ) : (
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  <tr>
                    <th className="px-2 py-2 text-left border-b border-gray-200 dark:border-gray-700">
                      {tr("history.table.index", { defaultValue: "#" })}
                    </th>
                    <th className="px-2 py-2 text-left border-b border-gray-200 dark:border-gray-700">
                      {tr("history.table.action", { defaultValue: "Action" })}
                    </th>
                    <th className="px-2 py-2 text-right border-b border-gray-200 dark:border-gray-700">
                      {" "}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {actionHistory.map((a, idx) => (
                    <tr key={idx} className="group hover:bg-gray-50 dark:hover:bg-gray-800/60">
                      <td className="px-2 py-2 border-b border-gray-100 dark:border-gray-800 text-center">
                        {idx + 1}
                      </td>
                      <td className="px-2 py-2 border-b border-gray-100 dark:border-gray-800">
                        {formatLastAction(a)}
                      </td>
                      <td className="px-2 py-2 border-b border-gray-100 dark:border-gray-800">
                        <div className="flex justify-end">
                          <button
                            onClick={() => handleUndoAt(idx)}
                            className="p-2 rounded-xl hover:bg-red-50 dark:hover:bg-red-900/30 text-red-600 dark:text-red-400 active:scale-[0.98]"
                            title={tr("history.table.undoTitle", { defaultValue: "Annuler cette action" })}
                            aria-label={tr("history.table.undoAria", {
                              index: idx + 1,
                              defaultValue: "Annuler l'action #{{index}}",
                            })}
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>

      {/* Bottom action bar (mobile) */}
      <div
        className="
          fixed bottom-0 left-0 right-0 z-40
          bg-white/90 dark:bg-gray-950/90 backdrop-blur
          border-t border-gray-200 dark:border-gray-800
        "
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        <div className="max-w-4xl mx-auto px-3 py-3 flex items-center gap-2">
          <button
            onClick={() => setActiveTab("live")}
            className={`flex-1 py-3 rounded-2xl text-sm font-semibold border active:scale-[0.98] ${
              activeTab === "live"
                ? "bg-orange-600 text-white border-orange-600"
                : "bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 border-gray-200 dark:border-gray-800"
            }`}
          >
            {tr("tabs.live", { defaultValue: "Live" })}
          </button>
          <button
            onClick={() => setActiveTab("stats")}
            className={`flex-1 py-3 rounded-2xl text-sm font-semibold border active:scale-[0.98] ${
              activeTab === "stats"
                ? "bg-orange-600 text-white border-orange-600"
                : "bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 border-gray-200 dark:border-gray-800"
            }`}
          >
            {tr("tabs.statsShort", { defaultValue: "Stats" })}
          </button>
          <button
            onClick={() => setActiveTab("history")}
            className={`flex-1 py-3 rounded-2xl text-sm font-semibold border active:scale-[0.98] ${
              activeTab === "history"
                ? "bg-orange-600 text-white border-orange-600"
                : "bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 border-gray-200 dark:border-gray-800"
            }`}
          >
            {tr("tabs.historyShort", { defaultValue: "Historique" })}
          </button>
        </div>
      </div>

      {/* Modal éditeur d’effectif (scrollable + safe area) */}
      {showRosterEditor && (
        <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center">
          <div className="absolute inset-0 bg-black/60" onClick={closeRosterEditor} />
          <div
            className="
              relative z-10 w-full sm:max-w-2xl
              bg-white dark:bg-gray-900
              rounded-t-3xl sm:rounded-3xl
              border border-gray-200 dark:border-gray-800
              shadow-xl
              max-h-[90dvh]
              overflow-hidden
            "
            style={{
              paddingBottom: "env(safe-area-inset-bottom)",
              paddingTop: "env(safe-area-inset-top)",
            }}
          >
            {/* Header sticky */}
            <div className="sticky top-0 z-10 bg-white/90 dark:bg-gray-900/90 backdrop-blur border-b border-gray-200 dark:border-gray-800 px-4 py-3 flex items-center justify-between">
              <h3 className="text-base sm:text-lg font-semibold">
                {currentSide === "team"
                  ? tr("roster.title", { defaultValue: "Joueurs présents" })
                  : tr("roster.titleOpp", { defaultValue: "Joueurs adverses" })}
              </h3>
              <button
                onClick={closeRosterEditor}
                className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 active:scale-[0.98]"
                aria-label={tr("roster.closeAria", { defaultValue: "Fermer" })}
              >
                <X size={18} />
              </button>
            </div>

            {/* Body scrollable */}
            <div className="overflow-y-auto overscroll-contain px-4 py-4 max-h-[calc(90dvh-64px)]">
              {rosterLoading ? (
                <div className="h-40 flex items-center justify-center rounded-2xl border border-gray-200 dark:border-gray-800">
                  <div className="inline-flex items-center gap-2 text-sm opacity-80">
                    <Loader2 size={16} className="animate-spin" />
                    {tr("roster.loading", { defaultValue: "Chargement de l’effectif…" })}
                  </div>
                </div>
              ) : (
                <div className="rounded-2xl border border-gray-200 dark:border-gray-800 overflow-hidden">
                  <div className="max-h-72 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0 z-10">
                        <tr>
                          <th className="px-2 py-2 text-left">
                            {tr("roster.table.present", { defaultValue: "Présent" })}
                          </th>
                          <th className="px-2 py-2 text-left">
                            {tr("roster.table.number", { defaultValue: "#" })}
                          </th>
                          <th className="px-2 py-2 text-left">
                            {tr("roster.table.name", { defaultValue: "Nom" })}
                          </th>
                          <th className="px-2 py-2 text-left">
                            {tr("roster.table.position", { defaultValue: "Poste" })}
                          </th>
                          <th className="px-2 py-2 text-right">
                            {tr("roster.table.remove", { defaultValue: "Retirer" })}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {tempPlayers.map((p) => (
                          <tr key={p.number} className="border-t border-gray-100 dark:border-gray-800">
                            <td className="px-2 py-2">
                              <input
                                type="checkbox"
                                checked={p.active}
                                onChange={() => toggleTempPlayer(p.number)}
                                disabled={rosterLoading}
                                className="w-5 h-5"
                              />
                            </td>
                            <td className="px-2 py-2 font-medium">#{p.number}</td>
                            <td className="px-2 py-2">
                              {p.firstName} {p.lastName}
                            </td>
                            <td className="px-2 py-2">{p.position}</td>
                            <td className="px-2 py-2 text-right">
                              <button
                                className="text-red-600 hover:text-red-700 underline-offset-2 hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
                                onClick={() => removeTempPlayer(p.number)}
                                title={tr("roster.table.removeTitle", {
                                  defaultValue: "Supprimer ce joueur de la liste",
                                })}
                                disabled={rosterLoading}
                              >
                                {tr("roster.table.remove", { defaultValue: "Retirer" })}
                              </button>
                            </td>
                          </tr>
                        ))}
                        {tempPlayers.length === 0 && (
                          <tr>
                            <td className="px-2 py-6 text-center text-sm opacity-70" colSpan={5}>
                              {tr("roster.empty", {
                                defaultValue: "Aucun joueur dans la liste. Ajoute-en ci-dessous.",
                              })}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Ajout rapide d'un joueur (inputs touch-friendly) */}
              <div className="mt-4 rounded-2xl border border-gray-200 p-3 dark:border-gray-800">
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                  <input
                    className="px-3 py-2 h-11 rounded-xl border bg-white border-gray-300 dark:bg-gray-800 dark:border-gray-700 text-base"
                    placeholder={tr("roster.quickAdd.first", { defaultValue: "Prénom" })}
                    value={newPFirst}
                    onChange={(e) => setNewPFirst(e.target.value)}
                  />
                  <input
                    className="px-3 py-2 h-11 rounded-xl border bg-white border-gray-300 dark:bg-gray-800 dark:border-gray-700 text-base"
                    placeholder={tr("roster.quickAdd.last", { defaultValue: "Nom" })}
                    value={newPLast}
                    onChange={(e) => setNewPLast(e.target.value)}
                  />
                  <input
                    className="px-3 py-2 h-11 rounded-xl border bg-white border-gray-300 dark:bg-gray-800 dark:border-gray-700 text-base"
                    placeholder={tr("roster.quickAdd.number", { defaultValue: "Numéro" })}
                    type="number"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={newPNumber}
                    onChange={(e) =>
                      setNewPNumber(e.target.value === "" ? "" : parseInt(e.target.value, 10))
                    }
                  />
                  <input
                    className="px-3 py-2 h-11 rounded-xl border bg-white border-gray-300 dark:bg-gray-800 dark:border-gray-700 text-base"
                    placeholder={tr("roster.quickAdd.position", { defaultValue: "Poste (ex: G/F/C)" })}
                    value={newPPos}
                    onChange={(e) => setNewPPos(e.target.value)}
                  />
                  <button
                    onClick={handleQuickAdd}
                    className="inline-flex items-center justify-center gap-2 px-3 py-2 h-11 rounded-xl border border-gray-300 bg-white hover:bg-gray-50 text-sm dark:bg-gray-800 dark:border-gray-700 dark:hover:bg-gray-700 active:scale-[0.98]"
                    title={tr("roster.quickAdd.addTitle", { defaultValue: "Ajouter le joueur" })}
                  >
                    <Plus size={16} /> {tr("roster.quickAdd.add", { defaultValue: "Ajouter" })}
                  </button>
                </div>
              </div>

              {/* Actions */}
              <div className="mt-4 flex flex-col sm:flex-row justify-end gap-2">
                <button
                  onClick={closeRosterEditor}
                  className="px-4 py-3 rounded-2xl border border-gray-300 bg-white hover:bg-gray-50 text-sm dark:bg-gray-800 dark:border-gray-700 dark:hover:bg-gray-700 active:scale-[0.98]"
                >
                  {tr("common:cancel", { defaultValue: "Annuler" })}
                </button>
                <button
                  onClick={saveRoster}
                  disabled={rosterLoading}
                  className={`px-4 py-3 rounded-2xl bg-orange-600 text-white text-sm font-semibold active:scale-[0.98] ${
                    rosterLoading ? "opacity-60 cursor-not-allowed" : "hover:bg-orange-700"
                  }`}
                >
                  {rosterLoading ? (
                    <span className="inline-flex items-center gap-2">
                      <Loader2 size={16} className="animate-spin" />
                      {tr("common:saving", { defaultValue: "Enregistrement…" })}
                    </span>
                  ) : (
                    tr("common:save", { defaultValue: "Enregistrer" })
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal terrain (UNIQUEMENT mon équipe) */}
      {showCourt && currentSide === "team" && (
        <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center">
          <div className="absolute inset-0 bg-black/70" onClick={() => setShowCourt(false)} />
          <div
            className="
              relative z-10 w-full sm:w-auto
              bg-white dark:bg-gray-950
              rounded-t-3xl sm:rounded-3xl
              overflow-hidden
              max-h-[92dvh]
              border border-gray-200 dark:border-gray-800
              shadow-2xl
            "
            style={{
              paddingBottom: "env(safe-area-inset-bottom)",
              paddingTop: "env(safe-area-inset-top)",
            }}
          >
            <div className="sticky top-0 z-10 flex items-center justify-between px-4 py-3 bg-white/90 dark:bg-gray-950/90 backdrop-blur border-b border-gray-200 dark:border-gray-800">
              <div className="text-sm font-semibold">
                {tr("court.title", { defaultValue: "Emplacement du tir" })}
              </div>
              <button
                onClick={() => setShowCourt(false)}
                className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 active:scale-[0.98]"
                aria-label={tr("court.closeAria", { defaultValue: "Fermer le terrain" })}
              >
                <X size={18} />
              </button>
            </div>

            <div className="p-3">
              <div className="relative">
                <img
                  src="/court.png"
                  alt={tr("court.alt", { defaultValue: "Terrain de basket" })}
                  onClick={handleCourtClick}
                  className="w-[720px] max-w-[92vw] max-h-[72dvh] h-auto border-4 border-white rounded-2xl shadow-xl cursor-crosshair select-none"
                  draggable={false}
                />
                {feedbackShot && (
                  <div
                    className="absolute"
                    style={{
                      left: `${feedbackShot.x}%`,
                      top: `${feedbackShot.y}%`,
                      transform: "translate(-50%, -50%)",
                      width: 26,
                      height: 26,
                      backgroundColor: feedbackShot.made ? "green" : "red",
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "white",
                      fontWeight: 900,
                      zIndex: 10,
                      pointerEvents: "none",
                    }}
                  >
                    {feedbackShot.made ? "✓" : "×"}
                  </div>
                )}
              </div>

              <div className="mt-3 text-xs opacity-80">
                {tr("court.hint", { defaultValue: "Touchez le terrain pour placer le tir." })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal choix du joueur (déclenchée par un bouton de stat) */}
      {showPlayerPicker && (
        <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center">
          <div
            className="absolute inset-0 bg-black/70"
            onClick={() => {
              setShowPlayerPicker(false);
              setPendingAction(null);
            }}
          />
          <div
            className="
              relative z-10 w-full sm:w-[28rem] sm:max-w-[92vw]
              bg-white dark:bg-gray-950
              rounded-t-3xl sm:rounded-3xl
              overflow-hidden
              max-h-[85dvh]
              border border-gray-200 dark:border-gray-800
              shadow-2xl
              flex flex-col
            "
            style={{
              paddingBottom: "env(safe-area-inset-bottom)",
              paddingTop: "env(safe-area-inset-top)",
            }}
          >
            <div className="sticky top-0 z-10 flex items-center justify-between px-4 py-3 bg-white/90 dark:bg-gray-950/90 backdrop-blur border-b border-gray-200 dark:border-gray-800">
              <div className="text-sm font-semibold">
                {currentSide === "team"
                  ? tr("live.pickPlayerTitle", { defaultValue: "Choisissez le joueur :" })
                  : tr("live.pickOpponentPlayerTitle", { defaultValue: "Choisissez le joueur adverse :" })}
              </div>
              <button
                onClick={() => {
                  setShowPlayerPicker(false);
                  setPendingAction(null);
                }}
                className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 active:scale-[0.98]"
                aria-label={tr("live.pickPlayerCloseAria", { defaultValue: "Fermer" })}
              >
                <X size={18} />
              </button>
            </div>

            <div className="p-3 overflow-y-auto grid grid-cols-2 sm:grid-cols-3 gap-2">
              {currentSide === "opponent" && (
                <button
                  type="button"
                  onClick={() => pickPlayerAndDispatch(null)}
                  className="p-3 rounded-xl border text-left min-h-[64px] bg-white text-gray-800 border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-700 dark:hover:bg-gray-700 active:scale-[0.98]"
                >
                  <div className="font-semibold">
                    {tr("stats.labels.opponentTeam", { defaultValue: "Équipe adverse" })}
                  </div>
                  <div className="text-xs opacity-80">
                    {tr("live.opponentTeamButtonHint", { defaultValue: "Stats sans joueur (global)" })}
                  </div>
                </button>
              )}

              {activePlayers.map((p) => (
                <button
                  key={p.number}
                  type="button"
                  onClick={() => pickPlayerAndDispatch(p)}
                  className="p-3 rounded-xl border text-left min-h-[64px] bg-white text-gray-800 border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-700 dark:hover:bg-gray-700 active:scale-[0.98]"
                >
                  <div className="font-semibold">
                    #{p.number} {p.firstName} {p.lastName}
                  </div>
                  <div className="text-xs opacity-80">{p.position}</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};
